import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { hashPassword } from '@/utils/crypto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [pinBootstrapToken, setPinBootstrapToken] = useState(localStorage.getItem('pin_bootstrap_token'));
  const [pinRestaurant, setPinRestaurant] = useState(() => {
    try {
      const raw = localStorage.getItem('pin_restaurant');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (pinBootstrapToken) {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [token, pinBootstrapToken]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setRestaurant(response.data.restaurant);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Hash password with SHA-256 before transmission
    const passwordHash = await hashPassword(password);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email, 
        password: passwordHash 
      });

      if (response.data?.requires_pin) {
        beginPinBootstrap(response.data);
        return response.data;
      }

      applyAuthResponse(response.data);
      
      return response.data;
    } catch (error) {
      // Handle 426 Upgrade Required - legacy user needs migration
      if (error.response?.status === 426) {
        console.log('Legacy user detected, migrating to pw_scheme=2...');
        
        // Call migrate-login endpoint with original plaintext password (ONE FINAL TIME)
        const migrateResponse = await axios.post(`${API}/auth/migrate-login`, {
          email,
          password // Send plaintext for migration
        });
        
        applyAuthResponse(migrateResponse.data);
        
        console.log('Migration successful! Future logins will use SHA-256 hashing.');
        return migrateResponse.data;
      }
      
      // Re-throw other errors
      throw error;
    }
  };

  const register = async (data) => {
    // Hash password with SHA-256 before transmission
    const passwordHash = await hashPassword(data.password);

    const response = await axios.post(`${API}/auth/register`, {
      ...data,
      password: passwordHash,
    });

    if (response.data?.waitlist) {
      return { waitlist: true, message: response.data.message };
    }

    applyAuthResponse(response.data);

    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('pin_bootstrap_token');
    localStorage.removeItem('pin_restaurant');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setPinBootstrapToken(null);
    setPinRestaurant(null);
    setUser(null);
    setRestaurant(null);
  };

  /** Apply login/register response (token + user + optional restaurant). */
  const applyAuthResponse = (data) => {
    const { token: newToken, user: userData, restaurant: restaurantData } = data;
    localStorage.removeItem('pin_bootstrap_token');
    localStorage.removeItem('pin_restaurant');
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setPinBootstrapToken(null);
    setPinRestaurant(null);
    setUser(userData);
    setRestaurant(restaurantData ?? null);
  };

  const beginPinBootstrap = (data) => {
    const bootstrapToken = data?.token;
    if (!bootstrapToken) return;
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    localStorage.setItem('pin_bootstrap_token', bootstrapToken);
    if (data?.restaurant) {
      localStorage.setItem('pin_restaurant', JSON.stringify(data.restaurant));
      setPinRestaurant(data.restaurant);
    } else {
      localStorage.removeItem('pin_restaurant');
      setPinRestaurant(null);
    }
    setToken(null);
    setUser(null);
    setRestaurant(null);
    setPinBootstrapToken(bootstrapToken);
  };

  const completePinLogin = async (pin) => {
    if (!pinBootstrapToken) {
      throw new Error('No pending PIN login session');
    }
    const response = await axios.post(
      `${API}/auth/pin-login`,
      { pin },
      { headers: { Authorization: `Bearer ${pinBootstrapToken}` } }
    );
    applyAuthResponse(response.data);
    return response.data;
  };

  const switchUser = async () => {
    const response = await axios.post(`${API}/auth/pin-bootstrap`);
    beginPinBootstrap(response.data);
    return response.data;
  };

  const updateRestaurant = (data) => {
    setRestaurant(prev => ({ ...prev, ...data }));
  };

  const isPlatformAdmin = user?.role === 'platform_admin';
  const isTenantUser =
    !!user && user.role !== 'platform_admin' && !!restaurant;

  return (
    <AuthContext.Provider value={{
      user,
      restaurant,
      token,
      loading,
      login,
      register,
      logout,
      switchUser,
      completePinLogin,
      applyAuthResponse,
      updateRestaurant,
      isAuthenticated: !!token && !!user,
      isPlatformAdmin,
      isTenantUser,
      pinPending: !!pinBootstrapToken && !token,
      pinRestaurant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
